import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { useCurrentUser } from 'hooks/api/use-current-user';
import toast from 'components/partials/toast/toast';

import {
  applyModeledAdjustmentBatch,
  getModeledAdjustmentGrid,
  getGetModeledAdjustmentGridQueryKey,
  previewModeledAdjustmentGrid,
  previewModeledAdjustments,
} from 'api/generated/aid-simulation-modeled-adjustments/aid-simulation-modeled-adjustments';
import type {
  AidModelResultsResponseDto,
  CustomPopulationAdjustmentActionDto,
  CustomPopulationRequest,
  ModeledAdjustmentActionDto,
  ModeledAdjustmentBatchRequest,
  ModeledAdjustmentBatchResponse,
  ModeledAdjustmentGridBucketDto,
  ModeledAdjustmentGridDto,
  ModeledAdjustmentGridRowDto,
  ModeledAdjustmentPreviewRequestDto,
} from 'api/generated/schemas';
import { ModeledAdjustmentActionDtoAction } from 'api/generated/schemas/modeledAdjustmentActionDtoAction';
import { ModeledAdjustmentActionDtoAdjustmentType } from 'api/generated/schemas/modeledAdjustmentActionDtoAdjustmentType';

import { getGetAidModelResultsQueryKey } from '../../../hooks/use-aid-results';
import {
  useRegimes,
  useInvalidateRegimeComparison,
} from '../../../hooks/use-aid-regimes';

import {
  getPopulationTabKey,
  getPriorityEntryKey,
  getRegimeTabKey,
  initPopulationDraft,
  makeNewPopulationKey,
  toDraftDtos,
  toPriorityEntry,
  buildPopulationActions,
} from '../helpers/custom-population';
import type {
  DraftPopulation,
  NotIncludedStudents,
  PopulationDraftState,
  PopulationTab,
} from '../helpers/custom-population';
import { buildVolumeChange } from '../helpers/results-format';
import type { ChangeValue } from '../results-step.types';

type RowKey = `${string}|${number}|${number}`;

interface DirtyCell {
  admitMultiplier: number;
  yieldMultiplier: number;
}

type DirtyState = Record<RowKey, Partial<DirtyCell>>;

interface ParsedRowKey {
  ownerKey: string;
  bucketRank: number;
  academicRank: number;
}

interface CellEntry {
  adjustmentType: ModeledAdjustmentActionDtoAdjustmentType;
  academicRank: number;
  bucketRank: number;
  multiplier: number;
}

interface RegimeCellEntry extends CellEntry {
  regimeId: number;
}

interface PopulationCellEntry extends CellEntry {
  customPopulationId?: number;
  customPopulationKey?: string;
}

const ROW_KEY_SEPARATOR = '|';
const REGIME_KEY_PREFIX = 'regime:';
const PERSISTED_POPULATION_KEY_PREFIX = 'population:';
const DRAFT_POPULATION_KEY_PREFIX = 'draft:';
const DEFAULT_AWARD_TYPE = 'NEED';

const buildRowKey = (
  ownerKey: string,
  bucketRank: number,
  academicRank: number
): RowKey =>
  `${ownerKey}${ROW_KEY_SEPARATOR}${bucketRank}${ROW_KEY_SEPARATOR}${academicRank}`;

const parseRowKey = (key: string): ParsedRowKey => {
  const [ownerKey, bucketRank, academicRank] = key.split(ROW_KEY_SEPARATOR);

  return {
    ownerKey,
    bucketRank: Number(bucketRank),
    academicRank: Number(academicRank),
  };
};

const weightedAverage = (
  rows: AdmitAdjustmentRow[],
  valueFn: (r: AdmitAdjustmentRow) => number,
  weightFn: (r: AdmitAdjustmentRow) => number
): number => {
  const totalWeight = rows.reduce((sum, r) => sum + weightFn(r), 0);

  if (!totalWeight) return 0;

  return (
    rows.reduce((sum, r) => sum + valueFn(r) * weightFn(r), 0) / totalWeight
  );
};

const isSamePopulationDraft = (
  a: PopulationDraftState,
  b: PopulationDraftState
): boolean =>
  a.priorityOrder.map(getPriorityEntryKey).join(',') ===
    b.priorityOrder.map(getPriorityEntryKey).join(',') &&
  JSON.stringify(a.populations) === JSON.stringify(b.populations);

export interface AdmitAdjustmentRow {
  ownerKey: string;
  bucketRank: number;
  academicRank: number;
  pyAdmits: number;
  admitTarget: number;
  pyYield: number;
  projYield: number;
  admitMultiplier: number;
  yieldMultiplier: number;
  baselineAdmitMultiplier: number;
  baselineYieldMultiplier: number;
  isDirty: boolean;
}

export interface NeedBucketGroup {
  bucketRank: number;
  label: string;
  rows: AdmitAdjustmentRow[];
  pyYieldAvg: number;
  projYieldAvg: number;
}

export interface UseAdmitAdjustmentsResult {
  userId: string;
  modelId: number;
  isLoading: boolean;
  isError: boolean;
  tabs: PopulationTab[];
  activeTabKey: string | null;
  activeAwardType: string;
  setActiveTabKey: (key: string) => void;
  bucketGroups: NeedBucketGroup[];
  notIncludedStudents: NotIncludedStudents;
  newAdmits: ChangeValue | null;
  isDirtyGlobal: boolean;
  isSaving: boolean;
  isPreviewing: boolean;
  handleAdmitMultiplierChange: (
    ownerKey: string,
    bucketRank: number,
    academicRank: number,
    value: number
  ) => void;
  handleYieldMultiplierChange: (
    ownerKey: string,
    bucketRank: number,
    academicRank: number,
    value: number
  ) => void;
  handleRowUndo: (
    ownerKey: string,
    bucketRank: number,
    academicRank: number
  ) => void;
  handleRevertAll: () => void;
  handleSaveDraft: () => void;
  handleAddPopulation: (definition: CustomPopulationRequest) => void;
  handleUpdatePopulation: (
    tabKey: string,
    definition: CustomPopulationRequest
  ) => void;
  handleRemovePopulation: (tabKey: string) => void;
  handleReorderTabs: (activeKey: string, overKey: string) => void;
  previewResults: AidModelResultsResponseDto | null;
}

export const useAdmitAdjustments = (): UseAdmitAdjustmentsResult => {
  const { modelId: modelIdParam } = useParams();
  const modelId = modelIdParam ? Number(modelIdParam) : 0;
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser?.oktaUserId ?? '';
  const queryClient = useQueryClient();
  const invalidateRegimeComparison = useInvalidateRegimeComparison();

  const [activeTabKey, setActiveTabKey] = useState<string | null>(null);
  const [dirtyState, setDirtyState] = useState<DirtyState>({});
  const [populationDraft, setPopulationDraft] =
    useState<PopulationDraftState | null>(null);
  const [previewResults, setPreviewResults] =
    useState<AidModelResultsResponseDto | null>(null);
  const nextPopulationIndexRef = useRef(0);

  const gridQuery = useQuery<ModeledAdjustmentGridDto>({
    queryKey: getGetModeledAdjustmentGridQueryKey(userId, modelId),
    queryFn: ({ signal }) =>
      getModeledAdjustmentGrid(userId, modelId, undefined, signal),
    enabled: !!userId && !!modelId,
    retry: false,
  });

  const regimesQuery = useRegimes({
    userId,
    modelId,
    enabled: !!userId && !!modelId,
  });

  const persistedDraft = useMemo(
    () => initPopulationDraft(gridQuery.data),
    [gridQuery.data]
  );

  const isStructureDirty =
    !!populationDraft &&
    !isSamePopulationDraft(populationDraft, persistedDraft);

  const previewGridRequest = useMemo<ModeledAdjustmentPreviewRequestDto | null>(
    () =>
      isStructureDirty && populationDraft
        ? {
            cells: [],
            customPopulations: toDraftDtos(populationDraft),
            priorityOrder: populationDraft.priorityOrder,
            customPopulationCells: [],
          }
        : null,
    [isStructureDirty, populationDraft]
  );

  const previewGridQuery = useQuery<ModeledAdjustmentGridDto>({
    queryKey: [
      'modeled-adjustment-grid-preview',
      userId,
      modelId,
      previewGridRequest,
    ],
    queryFn: () =>
      previewModeledAdjustmentGrid(userId, modelId, previewGridRequest!),
    enabled: !!userId && !!modelId && !!previewGridRequest,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const previewGridErrorNotifiedAtRef = useRef(0);

  useEffect(() => {
    if (
      !previewGridRequest ||
      !previewGridQuery.isError ||
      previewGridQuery.errorUpdatedAt <= previewGridErrorNotifiedAtRef.current
    ) {
      return;
    }

    previewGridErrorNotifiedAtRef.current = previewGridQuery.errorUpdatedAt;
    toast.error({
      title: 'Preview failed',
      message: 'Could not refresh adjustment data for your population changes.',
    });
  }, [
    previewGridRequest,
    previewGridQuery.isError,
    previewGridQuery.errorUpdatedAt,
  ]);

  const grid = previewGridRequest
    ? previewGridQuery.isError
      ? gridQuery.data
      : previewGridQuery.data ?? gridQuery.data
    : gridQuery.data;

  const workingDraft = populationDraft ?? persistedDraft;

  const tabs = useMemo<PopulationTab[]>(() => {
    const regimeByKey = new Map(
      (grid?.regimes ?? []).map((regime) => [
        getRegimeTabKey(regime.regimeId!),
        regime,
      ])
    );
    const gridByPopulationKey = new Map(
      (grid?.customPopulations ?? []).map((entry) => [
        getPopulationTabKey({
          id: entry.population?.id,
          clientKey: entry.customPopulationKey,
        }),
        entry,
      ])
    );
    const populationByKey = new Map(
      workingDraft.populations.map((population) => [
        getPopulationTabKey(population),
        population,
      ])
    );
    const order =
      workingDraft.priorityOrder.length || !grid
        ? workingDraft.priorityOrder
        : grid.priorityOrder ?? [];

    return order.flatMap((entry): PopulationTab[] => {
      const key = getPriorityEntryKey(entry);
      const regime = regimeByKey.get(key);

      if (regime) {
        return [
          {
            key,
            kind: 'regime',
            regimeId: regime.regimeId!,
            name: regime.name ?? '',
          },
        ];
      }

      const population = populationByKey.get(key);

      if (!population) return [];

      return [
        {
          key,
          kind: 'custom',
          name: population.definition.name ?? '',
          population,
          grid: gridByPopulationKey.get(key) ?? { needBuckets: [] },
        },
      ];
    });
  }, [grid, workingDraft]);

  const getNeedBuckets = useCallback(
    (tab: PopulationTab): ModeledAdjustmentGridBucketDto[] =>
      (tab.kind === 'custom'
        ? tab.grid.needBuckets
        : grid?.regimes?.find((regime) => regime.regimeId === tab.regimeId)
            ?.needBuckets) ?? [],
    [grid]
  );

  const effectiveTabKey = useMemo(() => {
    if (activeTabKey !== null && tabs.some((tab) => tab.key === activeTabKey)) {
      return activeTabKey;
    }

    return tabs[0]?.key ?? null;
  }, [activeTabKey, tabs]);

  const findBaseRow = useCallback(
    (
      ownerKey: string,
      bucketRank: number,
      academicRank: number
    ): ModeledAdjustmentGridRowDto | undefined => {
      const tab = tabs.find((item) => item.key === ownerKey);

      return tab
        ? getNeedBuckets(tab)
            .find((bucket) => bucket.bucketRank === bucketRank)
            ?.rows?.find((row) => row.academicRank === academicRank)
        : undefined;
    },
    [tabs, getNeedBuckets]
  );

  const computeRow = useCallback(
    (
      ownerKey: string,
      bucketRank: number,
      row: ModeledAdjustmentGridRowDto
    ): AdmitAdjustmentRow => {
      const academicRank = row.academicRank ?? 0;
      const key = buildRowKey(ownerKey, bucketRank, academicRank);
      const dirty = dirtyState[key];
      const baseAdmitMult = row.admitMultiplier ?? 1;
      const baseYieldMult = row.yieldMultiplier ?? 1;
      const admitMult = dirty?.admitMultiplier ?? baseAdmitMult;
      const yieldMult = dirty?.yieldMultiplier ?? baseYieldMult;

      const pyAdmits = row.pyAdmits ?? 0;
      const admitTarget = Math.round(pyAdmits * admitMult);
      const pyYield = row.pyYield ?? 0;
      // Proj yield is a backend value (Σ projected expected-enroll / admit target)
      // and is not equal to pyYield. It scales linearly with the yield multiplier,
      // so derive the unit-multiplier value from the persisted baseline and re-apply
      // the effective multiplier to preview unsaved edits.
      const backendProjYield = row.projYield ?? 0;
      const projYieldAtUnitMultiplier = baseYieldMult
        ? backendProjYield / baseYieldMult
        : backendProjYield;
      const projYield = projYieldAtUnitMultiplier * yieldMult;

      return {
        ownerKey,
        bucketRank,
        academicRank,
        pyAdmits,
        admitTarget,
        pyYield,
        projYield,
        admitMultiplier: admitMult,
        yieldMultiplier: yieldMult,
        baselineAdmitMultiplier: baseAdmitMult,
        baselineYieldMultiplier: baseYieldMult,
        isDirty: admitMult !== baseAdmitMult || yieldMult !== baseYieldMult,
      };
    },
    [dirtyState]
  );

  const bucketGroups: NeedBucketGroup[] = useMemo(() => {
    const activeTab = tabs.find((tab) => tab.key === effectiveTabKey);

    if (!activeTab) return [];

    return getNeedBuckets(activeTab).map((bucket) => {
      const bucketRank = bucket.bucketRank ?? 0;
      const rows = (bucket.rows ?? []).map((row) =>
        computeRow(activeTab.key, bucketRank, row)
      );

      // PY yield never changes with unsaved edits, so use the backend rollup
      // directly. Proj yield is backend-derived too; only re-derive it locally
      // (weighted by admit target) while a row in this bucket has unsaved edits.
      const hasDirtyRow = rows.some((r) => r.isDirty);
      const pyYieldAvg = bucket.pyYield ?? 0;
      const projYieldAvg = hasDirtyRow
        ? weightedAverage(
            rows,
            (r) => r.projYield,
            (r) => r.admitTarget
          )
        : bucket.projYield ?? 0;

      return {
        bucketRank,
        label: bucket.label ?? '',
        rows,
        pyYieldAvg,
        projYieldAvg,
      };
    });
  }, [effectiveTabKey, tabs, getNeedBuckets, computeRow]);

  // Model-level rollup: every tab's rows, not just the active one's, so the
  // total stays in step with the global Apply adjustments / Revert changes actions.
  const newAdmits = useMemo(() => {
    let admitTargetTotal = 0;
    let pyAdmitsTotal = 0;

    for (const tab of tabs) {
      for (const bucket of getNeedBuckets(tab)) {
        for (const row of bucket.rows ?? []) {
          const computedRow = computeRow(tab.key, bucket.bucketRank ?? 0, row);

          admitTargetTotal += computedRow.admitTarget;
          pyAdmitsTotal += computedRow.pyAdmits;
        }
      }
    }

    return buildVolumeChange(admitTargetTotal, pyAdmitsTotal, 'count');
  }, [tabs, getNeedBuckets, computeRow]);

  const activeAwardType = useMemo(() => {
    const regimeData = (regimesQuery.data ?? []) as Array<{
      id?: number;
      awardType?: string;
    }>;
    const activeTab = tabs.find((tab) => tab.key === effectiveTabKey);
    const regimeId = activeTab?.kind === 'regime' ? activeTab.regimeId : null;
    const found =
      regimeId === null
        ? regimeData[0]
        : regimeData.find((r) => r.id === regimeId);

    return found?.awardType ?? DEFAULT_AWARD_TYPE;
  }, [effectiveTabKey, tabs, regimesQuery.data]);

  const isDirtyGlobal = Object.keys(dirtyState).length > 0 || isStructureDirty;

  const updateMultiplier = useCallback(
    (
      field: keyof DirtyCell,
      ownerKey: string,
      bucketRank: number,
      academicRank: number,
      value: number
    ) => {
      const key = buildRowKey(ownerKey, bucketRank, academicRank);
      const baseRow = findBaseRow(ownerKey, bucketRank, academicRank);
      const baseline: DirtyCell = {
        admitMultiplier: baseRow?.admitMultiplier ?? 1,
        yieldMultiplier: baseRow?.yieldMultiplier ?? 1,
      };
      const otherField: keyof DirtyCell =
        field === 'admitMultiplier' ? 'yieldMultiplier' : 'admitMultiplier';

      setDirtyState((prev) => {
        const otherValue = prev[key]?.[otherField];

        if (
          value === baseline[field] &&
          (otherValue === undefined || otherValue === baseline[otherField])
        ) {
          const { [key]: _removed, ...rest } = prev;

          return rest;
        }

        return {
          ...prev,
          [key]: { ...prev[key], [field]: value },
        };
      });
    },
    [findBaseRow]
  );

  const handleAdmitMultiplierChange = useCallback(
    (
      ownerKey: string,
      bucketRank: number,
      academicRank: number,
      value: number
    ) =>
      updateMultiplier(
        'admitMultiplier',
        ownerKey,
        bucketRank,
        academicRank,
        value
      ),
    [updateMultiplier]
  );

  const handleYieldMultiplierChange = useCallback(
    (
      ownerKey: string,
      bucketRank: number,
      academicRank: number,
      value: number
    ) =>
      updateMultiplier(
        'yieldMultiplier',
        ownerKey,
        bucketRank,
        academicRank,
        value
      ),
    [updateMultiplier]
  );

  const handleRowUndo = useCallback(
    (ownerKey: string, bucketRank: number, academicRank: number) => {
      const key = buildRowKey(ownerKey, bucketRank, academicRank);

      setDirtyState((prev) => {
        const { [key]: _removed, ...rest } = prev;

        return rest;
      });
    },
    []
  );

  const handleRevertAll = useCallback(() => {
    setDirtyState({});
    setPopulationDraft(null);
    setPreviewResults(null);
  }, []);

  const editDraft = useCallback(
    (updater: (draft: PopulationDraftState) => PopulationDraftState) =>
      setPopulationDraft((prev) => updater(prev ?? persistedDraft)),
    [persistedDraft]
  );

  const handleAddPopulation = useCallback(
    (definition: CustomPopulationRequest) => {
      const population: DraftPopulation = {
        clientKey: makeNewPopulationKey(nextPopulationIndexRef.current),
        definition,
      };

      nextPopulationIndexRef.current += 1;
      editDraft((draft) => ({
        populations: [...draft.populations, population],
        priorityOrder: [toPriorityEntry(population), ...draft.priorityOrder],
      }));
      setActiveTabKey(getPopulationTabKey(population));
      toast.success({ title: 'Custom admit adjustment added.' });
    },
    [editDraft]
  );

  const handleUpdatePopulation = useCallback(
    (tabKey: string, definition: CustomPopulationRequest) => {
      editDraft((draft) => ({
        ...draft,
        populations: draft.populations.map((population) =>
          getPopulationTabKey(population) === tabKey
            ? { ...population, definition }
            : population
        ),
      }));
      toast.success({ title: 'Custom admit adjustment saved.' });
    },
    [editDraft]
  );

  const handleRemovePopulation = useCallback(
    (tabKey: string) => {
      editDraft((draft) => ({
        populations: draft.populations.filter(
          (population) => getPopulationTabKey(population) !== tabKey
        ),
        priorityOrder: draft.priorityOrder.filter(
          (entry) => getPriorityEntryKey(entry) !== tabKey
        ),
      }));
      setDirtyState((prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(
            ([key]) => parseRowKey(key).ownerKey !== tabKey
          )
        )
      );
      toast.success({ title: 'Custom admit adjustment removed.' });
    },
    [editDraft]
  );

  const handleReorderTabs = useCallback(
    (activeKey: string, overKey: string) => {
      editDraft((draft) => {
        const keys = draft.priorityOrder.map(getPriorityEntryKey);
        const from = keys.indexOf(activeKey);
        const to = keys.indexOf(overKey);

        if (from < 0 || to < 0 || from === to) return draft;

        return {
          ...draft,
          priorityOrder: arrayMove(draft.priorityOrder, from, to),
        };
      });
    },
    [editDraft]
  );

  const buildCellEntries = useCallback(() => {
    const regimeCells: RegimeCellEntry[] = [];
    const populationCells: PopulationCellEntry[] = [];

    for (const [key, dirty] of Object.entries(dirtyState)) {
      const { ownerKey, bucketRank, academicRank } = parseRowKey(key);
      const multipliers = [
        [ModeledAdjustmentActionDtoAdjustmentType.ADMIT, dirty.admitMultiplier],
        [ModeledAdjustmentActionDtoAdjustmentType.YIELD, dirty.yieldMultiplier],
      ] as const;

      for (const [adjustmentType, multiplier] of multipliers) {
        if (multiplier === undefined) continue;

        if (ownerKey.startsWith(REGIME_KEY_PREFIX)) {
          regimeCells.push({
            regimeId: Number(ownerKey.slice(REGIME_KEY_PREFIX.length)),
            adjustmentType,
            academicRank,
            bucketRank,
            multiplier,
          });
        } else {
          populationCells.push({
            ...(ownerKey.startsWith(PERSISTED_POPULATION_KEY_PREFIX)
              ? {
                  customPopulationId: Number(
                    ownerKey.slice(PERSISTED_POPULATION_KEY_PREFIX.length)
                  ),
                }
              : {
                  customPopulationKey: ownerKey.slice(
                    DRAFT_POPULATION_KEY_PREFIX.length
                  ),
                }),
            adjustmentType,
            academicRank,
            bucketRank,
            multiplier,
          });
        }
      }
    }

    return { regimeCells, populationCells };
  }, [dirtyState]);

  const buildBatchRequest = useCallback((): ModeledAdjustmentBatchRequest => {
    const { regimeCells, populationCells } = buildCellEntries();
    const cellActions: ModeledAdjustmentActionDto[] = regimeCells.map(
      (cell) => ({
        action: ModeledAdjustmentActionDtoAction.SAVE,
        regimeId: cell.regimeId,
        adjustmentType: cell.adjustmentType,
        academicRank: cell.academicRank,
        bucketRank: cell.bucketRank,
        multiplier: cell.multiplier,
      })
    );
    const customPopulationCellActions: CustomPopulationAdjustmentActionDto[] =
      populationCells.map((cell) => ({
        action: ModeledAdjustmentActionDtoAction.SAVE,
        customPopulationId: cell.customPopulationId,
        customPopulationKey: cell.customPopulationKey,
        adjustmentType: cell.adjustmentType,
        academicRank: cell.academicRank,
        bucketRank: cell.bucketRank,
        multiplier: cell.multiplier,
      }));

    return {
      populationActions: isStructureDirty
        ? buildPopulationActions(
            populationDraft!,
            persistedDraft.populations.map((population) => population.id!)
          )
        : undefined,
      priorityOrder: isStructureDirty
        ? populationDraft!.priorityOrder
        : undefined,
      cellActions,
      customPopulationCellActions,
    };
  }, [buildCellEntries, isStructureDirty, populationDraft, persistedDraft]);

  const saveMutation = useMutation<
    ModeledAdjustmentBatchResponse,
    unknown,
    {
      oktaUserId: string;
      modelId: number;
      data: ModeledAdjustmentBatchRequest;
    }
  >({
    mutationFn: ({ oktaUserId, modelId: mId, data }) =>
      applyModeledAdjustmentBatch(oktaUserId, mId, data),
    onSuccess: () => {
      setDirtyState({});
      setPopulationDraft(null);
      setPreviewResults(null);
      queryClient.invalidateQueries({
        queryKey: getGetModeledAdjustmentGridQueryKey(userId, modelId),
      });
      queryClient.invalidateQueries({
        queryKey: getGetAidModelResultsQueryKey(userId, modelId),
      });
      invalidateRegimeComparison(userId, modelId);
      toast.success({
        title: 'Adjustments were saved',
        message: 'Updated values are baseline now.',
      });
    },
    onError: () => {
      toast.error({
        title: 'Could not save adjustments',
        message: 'Your changes were not saved. Try again.',
      });
    },
  });

  const previewMutation = useMutation<
    AidModelResultsResponseDto,
    unknown,
    {
      oktaUserId: string;
      modelId: number;
      data: ModeledAdjustmentPreviewRequestDto;
    }
  >({
    mutationFn: ({ oktaUserId, modelId: mId, data }) =>
      previewModeledAdjustments(oktaUserId, mId, data),
    onSuccess: (data) => {
      setPreviewResults(data);
    },
    onError: () => {
      setPreviewResults(null);
      toast.error({
        title: 'Preview failed',
        message: 'Could not refresh estimated results for your changes.',
      });
    },
  });

  const handleSaveDraft = useCallback(() => {
    const request = buildBatchRequest();

    const hasActions =
      !!request.populationActions?.length ||
      !!request.cellActions?.length ||
      !!request.customPopulationCellActions?.length;

    if (!hasActions && !request.priorityOrder) return;

    saveMutation.mutate({ oktaUserId: userId, modelId, data: request });
  }, [buildBatchRequest, saveMutation, userId, modelId]);

  const triggerPreview = useCallback(() => {
    const { regimeCells, populationCells } = buildCellEntries();

    if (!regimeCells.length && !populationCells.length && !isStructureDirty) {
      setPreviewResults(null);

      return;
    }

    previewMutation.mutate({
      oktaUserId: userId,
      modelId,
      data: {
        cells: regimeCells,
        customPopulations:
          isStructureDirty && populationDraft
            ? toDraftDtos(populationDraft)
            : undefined,
        priorityOrder:
          isStructureDirty && populationDraft
            ? populationDraft.priorityOrder
            : undefined,
        customPopulationCells: populationCells.length
          ? populationCells
          : undefined,
      },
    });
  }, [
    buildCellEntries,
    isStructureDirty,
    populationDraft,
    previewMutation,
    userId,
    modelId,
  ]);

  const triggerPreviewRef = useRef(triggerPreview);

  triggerPreviewRef.current = triggerPreview;

  const prevDirtyRef = useRef('');
  const currentDirtySerialized = JSON.stringify({
    dirtyState,
    populationDraft: isStructureDirty ? populationDraft : null,
  });
  const cleanSerialized = JSON.stringify({
    dirtyState: {},
    populationDraft: null,
  });

  useEffect(() => {
    if (currentDirtySerialized !== prevDirtyRef.current) {
      prevDirtyRef.current = currentDirtySerialized;

      if (currentDirtySerialized !== cleanSerialized) {
        triggerPreviewRef.current();
      } else {
        setPreviewResults(null);
      }
    }
  }, [currentDirtySerialized, cleanSerialized]);

  return {
    userId,
    modelId,
    isLoading: gridQuery.isLoading,
    isError:
      gridQuery.isError || (!!previewGridRequest && previewGridQuery.isError),
    tabs,
    activeTabKey: effectiveTabKey,
    activeAwardType,
    setActiveTabKey,
    bucketGroups,
    notIncludedStudents: {
      notIncluded: grid?.notIncludedStudents ?? 0,
      total: grid?.totalStudents ?? 0,
    },
    newAdmits,
    isDirtyGlobal,
    isSaving: saveMutation.isPending,
    isPreviewing: previewMutation.isPending,
    handleAdmitMultiplierChange,
    handleYieldMultiplierChange,
    handleRowUndo,
    handleRevertAll,
    handleSaveDraft,
    handleAddPopulation,
    handleUpdatePopulation,
    handleRemovePopulation,
    handleReorderTabs,
    previewResults,
  };
};
